import { Controller } from "@hotwired/stimulus"
import { viewerTimeZone } from "../lib/viewer_time_zone"

// Custom send-at panel inside the composer Send later menu. The short 5/10/30
// options stay on dropdown_controller; this only owns Tomorrow morning /
// Next Monday's sibling — Pick date & time — and the confirmation line.
//
// Custom times post a timezone-less datetime-local value. The server parses
// it in the reader's zone (lib/viewer_time_zone) — the same zone the presets
// use. JS must not turn that string into a Date and back (that would bake in
// the browser's offset).
export default class extends Controller {
  static targets = ["presets", "custom", "sendAt", "confirmation", "error"]

  showCustom() {
    this.presetsTarget.hidden = true
    this.customTarget.hidden = false
    this.sendAtTarget.disabled = false
    this.fillDefaultSendAt()
    this.updateConfirmation()
    this.menu.setAttribute("role", "dialog")
    this.menu.setAttribute("aria-label", "Pick date and time")
    this.sendAtTarget.focus()
  }

  showPresets() {
    this.reset()
    this.firstOption?.focus()
  }

  reset() {
    if (!this.hasCustomTarget) return

    this.customTarget.hidden = true
    this.presetsTarget.hidden = false
    this.sendAtTarget.disabled = true
    this.sendAtTarget.value = ""
    this.clearError()
    if (this.hasConfirmationTarget) {
      this.confirmationTarget.textContent = ""
      this.confirmationTarget.hidden = true
    }
    this.menu.setAttribute("role", "menu")
    this.menu.setAttribute("aria-label", "Send later")
  }

  confirm(event) {
    if (!this.customSendIsInTheFuture()) {
      event.preventDefault()
      this.showError("Choose a time in the future.")
    }
  }

  updateConfirmation() {
    if (!this.hasConfirmationTarget) return

    const stamp = this.sendAtTarget.value
    const label = stamp ? this.confirmationLabel(stamp) : ""
    this.confirmationTarget.textContent = label
    this.confirmationTarget.hidden = !label
    if (this.customSendIsInTheFuture()) this.clearError()
  }

  fillDefaultSendAt() {
    const soon = new Date(Date.now() + 60 * 60 * 1000)
    this.sendAtTarget.min = this.stampInAppZone(new Date())
    this.sendAtTarget.value = this.stampInAppZone(soon)
  }

  customSendIsInTheFuture() {
    const chosen = this.sendAtTarget.value
    if (!chosen) return false

    return chosen > this.stampInAppZone(new Date())
  }

  confirmationLabel(stamp) {
    const [date, time] = stamp.split("T")
    if (!date || !time) return ""

    const [year, month, day] = date.split("-").map(Number)
    const [hour, minute] = time.split(":").map(Number)
    const utc = new Date(Date.UTC(year, month - 1, day, hour, minute))
    const formatted = new Intl.DateTimeFormat("en-US", {
      timeZone: "UTC",
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit"
    }).format(utc)
    const zone = viewerTimeZone()

    return `Sends ${formatted} · ${zone}`
  }

  stampInAppZone(instant) {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: viewerTimeZone(),
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23"
    }).formatToParts(instant)

    const value = (type) => parts.find((part) => part.type === type)?.value

    return `${value("year")}-${value("month")}-${value("day")}T${value("hour")}:${value("minute")}`
  }

  showError(message) {
    this.errorTarget.hidden = false
    this.errorTarget.textContent = message
  }

  clearError() {
    this.errorTarget.hidden = true
    this.errorTarget.textContent = ""
  }

  get menu() {
    return this.element.querySelector("[data-dropdown-target='menu']")
  }

  get firstOption() {
    return this.element.querySelector("[role='menuitemradio'], [role='menuitem']")
  }
}
